import {
  React,
  useState,
  useEffect,
  useSelector,
  postRequestAuthenticated,
} from "../../../globals/imports.js";
import UpgradeBanner, { hasGrowth } from "../widgets/UpgradeBanner";
import { SkeletonBlocks } from "../widgets/Skeleton";

// Voice presets — MUST match the ids in the AI service (VOICE_PRESETS).
const VOICES = [
  { id: "warm-guide", label: "Warm guide", desc: "Warm and encouraging." },
  { id: "calm-mentor", label: "Calm mentor", desc: "Calm, patient, thoughtful." },
  { id: "friendly-peer", label: "Friendly peer", desc: "Casual and friendly." },
  { id: "crisp-professional", label: "Crisp & professional", desc: "Concise and to the point." },
  { id: "playful", label: "Playful", desc: "Light, upbeat energy." },
];

// Fixed abilities / guardrails — shown as pills (not configurable).
const CAN_DO = ["Suggest a channel", "Open a topic", "Open events", "Hand off to your team"];
const WONT_DO = [
  "Pricing or billing",
  "Other brands",
  "Off-topic chat",
  "Change embed or theme",
  "Generate images / files",
];

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? "bg-theme-accent" : "bg-theme-tertiaryBackground"
    } ${disabled ? "opacity-50" : ""}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const TriggerRow = ({ label, desc, checked, onChange }) => (
  <div className="flex items-center justify-between gap-4 border-b border-theme-chatDivider py-4 last:border-b-0">
    <div>
      <p className="text-sm font-medium text-theme-secondaryText">{label}</p>
      {desc && <p className="mt-0.5 text-xs text-theme-placeholder">{desc}</p>}
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
);

const Section = ({ title, desc, children }) => (
  <div className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
    <p className="text-sm font-semibold text-theme-secondaryText">{title}</p>
    {desc && <p className="mt-1 text-xs text-theme-placeholder">{desc}</p>}
    <div className="mt-4">{children}</div>
  </div>
);

const BotStudioTab = () => {
  const [bot, setBot] = useState({
    enabled: false,
    chatMention: false,
    name: "assistant",
    voice: "warm-guide",
    persona: "",
    personaRefined: "",
    triggers: {
      welcomeNewJoiners: true,
      reEngageIdle: false,
      reactToFirstPost: false,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [refining, setRefining] = useState(false);
  const business = useSelector((s) => s.business.business);
  const canUse = hasGrowth(business); // Bot Studio is Growth-only

  useEffect(() => {
    let active = true;
    postRequestAuthenticated("/embed/config", {})
      .then((res) => {
        if (active && res?.success && res.config?.bot) {
          const b = res.config.bot;
          setBot({
            enabled: !!b.enabled,
            chatMention: !!b.chatMention,
            name: b.name || "assistant",
            voice: b.voice || "warm-guide",
            persona: b.personaRefined || b.persona || "",
            personaRefined: b.personaRefined || "",
            triggers: {
              welcomeNewJoiners: b.triggers?.welcomeNewJoiners !== false,
              reEngageIdle: !!b.triggers?.reEngageIdle,
              reactToFirstPost: !!b.triggers?.reactToFirstPost,
            },
          });
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const setField = (k, v) => setBot((b) => ({ ...b, [k]: v }));
  const setTrigger = (k, v) =>
    setBot((b) => ({ ...b, triggers: { ...b.triggers, [k]: v } }));

  const refinePersona = async () => {
    if (!bot.persona.trim()) return;
    setRefining(true);
    try {
      const res = await postRequestAuthenticated("/bot/refine-persona", {
        persona: bot.persona,
      });
      if (res?.success && res.persona) setField("persona", res.persona);
    } finally {
      setRefining(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // The persona box IS the effective persona now — store it as both the raw
      // and the "refined" value the bot injects.
      await postRequestAuthenticated("/embed/config/update", {
        bot: { ...bot, personaRefined: bot.persona },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-2xl px-1 pt-6">
        <SkeletonBlocks count={4} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-5 px-1 pt-6 pb-24">
      {/* Header + master switch */}
      <div className="flex items-center justify-between rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground p-5">
        <div>
          <p className="text-base font-semibold text-theme-secondaryText">
            Bot Studio
          </p>
          <p className="mt-1 text-xs text-theme-placeholder">
            A community assistant that greets your members and helps them find
            their way around — pointing them to the right channels and topics.
          </p>
        </div>
        <Toggle
          checked={bot.enabled}
          onChange={(v) => setField("enabled", v)}
        />
      </div>

      {/* Summon in chat — the one Growth-only flag; kept at the top. */}
      <Section
        title="Summon in chat"
        desc={`Let members open the bot by typing @${
          bot.name || "assistant"
        } in a channel chat.`}
      >
        {!canUse && <UpgradeBanner feature="Summoning the bot in chat" />}
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-theme-secondaryText">
            Enable @{bot.name || "assistant"} in chat
          </p>
          <Toggle
            checked={bot.chatMention}
            onChange={(v) => setField("chatMention", v)}
            disabled={!canUse}
          />
        </div>
      </Section>

      {/* Identity */}
      <Section
        title="Identity"
        desc="How the bot is named and how it speaks."
      >
        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
              Handle (members type @this)
            </p>
            <div className="flex items-center rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3">
              <span className="text-sm text-theme-placeholder">@</span>
              <input
                value={bot.name}
                onChange={(e) =>
                  setField(
                    "name",
                    e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase()
                  )
                }
                placeholder="assistant"
                className="w-full bg-transparent px-1 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
              />
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
              Voice
            </p>
            <div className="flex flex-wrap gap-2">
              {VOICES.map((v) => {
                const active = bot.voice === v.id;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setField("voice", v.id)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? "border-theme-accent bg-theme-accent text-theme-accentText"
                        : "border-theme-chatDivider bg-theme-tertiaryBackground text-theme-secondaryText hover:border-theme-accent"
                    }`}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-theme-placeholder">
              {VOICES.find((v) => v.id === bot.voice)?.desc}
            </p>
          </div>
        </div>
      </Section>

      {/* Persona */}
      <Section
        title="Persona"
        desc="Describe the bot's character in your own words. Refine turns it into a clean, safe persona (we strip out any actions — the bot's abilities are fixed)."
      >
        <textarea
          value={bot.persona}
          onChange={(e) => setField("persona", e.target.value)}
          rows={4}
          placeholder="e.g. A warm, encouraging guide who loves helping newcomers feel at home and points people to the right discussions."
          className="w-full resize-none rounded-lg border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-2.5 text-sm text-theme-secondaryText outline-none placeholder:text-theme-placeholder"
        />
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={refinePersona}
            disabled={refining || !bot.persona.trim()}
            className="rounded-lg border border-theme-accent px-3 py-1.5 text-xs font-medium text-theme-accent disabled:opacity-50"
          >
            {refining ? "Refining…" : "Refine with AI"}
          </button>
          <span className="text-xs text-theme-placeholder">
            Rewrites the box above into a clean, safe persona.
          </span>
        </div>
      </Section>

      {/* Triggers */}
      <Section
        title="Proactive triggers"
        desc="When the bot reaches out on its own."
      >
        <TriggerRow
          label="Welcome new joiners"
          desc="Greet a member the first time they enter the community."
          checked={bot.triggers.welcomeNewJoiners}
          onChange={(v) => setTrigger("welcomeNewJoiners", v)}
        />
        <TriggerRow
          label="Re-engage idle members"
          desc="Reach out when a member returns after ~5 quiet days."
          checked={bot.triggers.reEngageIdle}
          onChange={(v) => setTrigger("reEngageIdle", v)}
        />
        <TriggerRow
          label="React to first post"
          desc="Congratulate a member the first time they post in a channel."
          checked={bot.triggers.reactToFirstPost}
          onChange={(v) => setTrigger("reactToFirstPost", v)}
        />
      </Section>

      {/* Abilities & guardrails — informational, fixed, shown as pills */}
      <Section
        title="Abilities & guardrails"
        desc="Fixed and deliberately strict — the bot stays in its lane."
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          Can do
        </p>
        <div className="flex flex-wrap gap-2">
          {CAN_DO.map((t) => (
            <span
              key={t}
              className="rounded-full border border-theme-accentSoft bg-theme-accentSoft px-3 py-1 text-xs font-medium text-theme-secondaryText"
            >
              {t}
            </span>
          ))}
        </div>
        <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-theme-placeholder">
          Won't do
        </p>
        <div className="flex flex-wrap gap-2">
          {WONT_DO.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-theme-chatDivider bg-theme-tertiaryBackground px-3 py-1 text-xs text-theme-placeholder"
            >
              <span className="text-theme-error">✕</span> {t}
            </span>
          ))}
        </div>
      </Section>

      {/* Save */}
      <div className="sticky bottom-0 flex items-center justify-end gap-3 border-t border-theme-chatDivider bg-theme-primaryBackground py-4">
        {saved && (
          <span className="text-sm text-theme-accent">Saved ✓</span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-theme-accent px-5 py-2.5 text-sm font-medium text-theme-accentText transition-colors hover:bg-theme-accentHover disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

export default BotStudioTab;
